/**
 * useMetaIntegration — Meta Lead Ads wizard state and actions.
 * Reads public config; actions call Edge Functions (token never stored client-side).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const META_EDGE_ORIGIN = (() => {
  try {
    const url = import.meta.env.VITE_SUPABASE_URL;
    if (url && typeof url === "string") {
      const u = new URL(url);
      return `${u.origin}/functions/v1`;
    }
  } catch {
    // ignore
  }
  return "unknown";
})();

/**
 * Invoke a Meta Edge Function with consistent error handling.
 * In DEV logs fn, body keys, origin. On error throws with message: "<fn> — <status> — <detail>".
 * Never logs or stores tokens.
 */
export async function metaInvokeEdge<T = unknown>(
  fn: string,
  body: Record<string, unknown>
): Promise<T> {
  const safeBody = { ...body };
  if (import.meta.env.DEV) {
    console.log("[metaInvokeEdge] Calling", fn, {
      bodyKeys: Object.keys(safeBody),
      origin: META_EDGE_ORIGIN,
    });
  }

  const { data, error } = await supabase.functions.invoke(fn, { body: safeBody });

  if (error) {
    const status = (error as { status?: number; context?: { status?: number } })?.context?.status
      ?? (error as { status?: number }).status
      ?? "non-2xx";
    const msg = error.message || "Edge function request failed.";
    const detail = `${fn} — ${status} — ${msg}`;
    if (import.meta.env.DEV) {
      console.error("[metaInvokeEdge] Error", { fn, status, message: error.message });
    }
    throw new Error(detail);
  }

  const payload = data as { error?: string; missing_env?: string[] };
  if (payload?.error != null || (Array.isArray(payload?.missing_env) && payload.missing_env.length > 0)) {
    const missing = payload.missing_env;
    const msg = missing?.length
      ? `Server configuration error: missing ${missing.join(", ")}`
      : (payload.error ?? "Server returned an error.");
    const detail = `${fn} — error — ${msg}`;
    if (import.meta.env.DEV) {
      console.error("[metaInvokeEdge] Function error", { fn, error: payload.error, missing_env: missing });
    }
    throw new Error(detail);
  }

  return data as T;
}

export interface MetaConfigRow {
  id: string;
  workspace_id: string;
  page_id: string;
  page_name: string | null;
  form_id: string | null;
  selected_form_id: string | null;
  is_enabled: boolean;
  status: string | null;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MetaFormOption {
  id: string;
  name: string;
}

export function useMetaIntegration(workspaceId: string | null) {
  const queryClient = useQueryClient();

  const {
    data: configRows,
    isLoading: configLoading,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ["meta-integration-config", workspaceId],
    queryFn: async (): Promise<MetaConfigRow[]> => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("integrations_meta_pages")
        .select("id, workspace_id, page_id, page_name, form_id, selected_form_id, is_enabled, status, last_sync_at, created_at, updated_at")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MetaConfigRow[];
    },
    enabled: !!workspaceId,
  });

  const config = configRows?.[0] ?? null;

  const testConnection = useMutation({
    mutationFn: async ({ page_id, page_access_token }: { page_id: string; page_access_token: string }) => {
      const data = await metaInvokeEdge<{ ok: boolean; page?: { id: string; name?: string }; error?: { message: string; code?: string } }>(
        "meta-test-connection",
        { page_id: page_id.trim(), page_access_token: page_access_token.trim() }
      );
      return data;
    },
  });

  const loadForms = useMutation({
    mutationFn: async (params: {
      page_id: string;
      page_access_token?: string;
      workspace_id?: string;
    }) => {
      const body: Record<string, unknown> = { page_id: params.page_id.trim() };
      if (params.page_access_token?.trim()) {
        body.page_access_token = params.page_access_token.trim();
      } else if (params.workspace_id?.trim()) {
        body.workspace_id = params.workspace_id.trim();
      }
      const data = await metaInvokeEdge<{ forms?: MetaFormOption[]; error?: string }>("meta-list-forms", body);
      return (data?.forms ?? []) as MetaFormOption[];
    },
  });

  const saveConnection = useMutation({
    mutationFn: async (params: {
      workspace_id: string;
      page_id: string;
      selected_form_id: string | null;
      enabled: boolean;
      page_access_token?: string;
    }) => {
      return await metaInvokeEdge<{ ok?: boolean; error?: string }>("meta-save-connection", params as unknown as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-integration-config", workspaceId] });
    },
  });

  const oauthStart = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error("No workspace");
      const data = await metaInvokeEdge<{ url?: string; error?: string }>("meta-oauth-start", {
        workspace_id: workspaceId,
      });
      if (!data?.url) throw new Error("meta-oauth-start — error — No OAuth URL returned");
      return data.url;
    },
  });

  const subscribePage = useMutation({
    mutationFn: async (params: { page_id: string; enabled: boolean }) => {
      if (!workspaceId) throw new Error("No workspace");
      return await metaInvokeEdge<{ ok?: boolean; error?: string }>("meta-subscribe-page", {
        workspace_id: workspaceId,
        page_id: params.page_id,
        enabled: params.enabled,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-integration-config", workspaceId] });
    },
  });

  const syncNow = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error("No workspace");
      return await metaInvokeEdge<{
        ok?: boolean;
        error?: string;
        inserted?: number;
        skipped?: number;
        errors?: number;
        last_sync_at?: string;
      }>("meta-sync-now", { workspace_id: workspaceId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-integration-config", workspaceId] });
    },
  });

  return {
    config,
    configRows: configRows ?? [],
    configLoading,
    refetchConfig,
    testConnection,
    loadForms,
    saveConnection,
    syncNow,
    oauthStart,
    subscribePage,
  };
}
